import React, { useState, useEffect } from 'react';
import { getOrganizationLeads } from '../../api';
import SettingsPanel from './SettingsPanel';
import { useNavigate } from 'react-router-dom';
import MobileNavigation from './Navigations/MobileNavigation';
import DesktopNavigation from './Navigations/DesktopNavigation';
import { updateTabTitle } from '../../utils/tabTitle';

// Device Detection Hook
const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    deviceType: 'desktop',
    orientation: 'landscape'
  });

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Mobile Detection
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isTabletUA = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
      
      // Touch Detection
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Screen Orientation
      const orientation = width > height ? 'landscape' : 'portrait';
      
      // Kombinierte Logik
      let deviceType = 'desktop';
      let isMobile = false;
      let isTablet = false;
      let isDesktop = true;
      
      if (isMobileUA && !isTabletUA) {
        deviceType = 'mobile';
        isMobile = true;
        isDesktop = false;
      } else if (isTabletUA || (hasTouch && width >= 768 && width <= 1024)) {
        deviceType = 'tablet';
        isTablet = true;
        isDesktop = false;
      } else if (hasTouch && width < 768) {
        deviceType = 'mobile';
        isMobile = true;
        isDesktop = false;
      }
      
      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        deviceType,
        orientation,
        hasTouch,
        screenWidth: width,
        screenHeight: height
      });
    };

    detectDevice();
    window.addEventListener('resize', detectDevice);
    window.addEventListener('orientationchange', detectDevice);

    return () => {
      window.removeEventListener('resize', detectDevice);
      window.removeEventListener('orientationchange', detectDevice);
    };
  }, []);

  return deviceInfo;
};

function DashboardNav({ 
  activeSection, 
  onSectionChange, 
  onLogout, 
  user,
  onOpenOrganizationManagement,
  onOpenMembersManagement,
  onViewOrganizationLeads,
  onViewTeamLeads,
  showOrgManagement,
  showMembersManagement,
  showOrganizationLeads
}) {
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  
  // Verwende den Device Detection Hook
  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet, isDesktop, deviceType, orientation } = deviceInfo;

  // Lead-Zähler States - NEUE KATEGORIEN
  const [leadCounts, setLeadCounts] = useState({
    all: 0,
    open: 0, // Ersetzt 'new' - Leads die noch nicht angerufen wurden
    onHold: 0, // Neue Kategorie - erstmal leer
    terminAbgesagt: 0, // Neue Kategorie - abgesagte Termine
    neKunden: 0, // Neue Kategorie - Nicht erreichte Kunden
    spaeterAnrufen: 0, // Neue Kategorie - Später anrufen
    ohneOptIn: 0, // NEUE KATEGORIE - Ohne Opt-In
    testLeads: 0, // NEUE KATEGORIE - Test Leads
    finalNichtErreicht: 0, // NEUE KATEGORIE - Final nicht erreicht
    finalAbgesagt: 0 // NEUE KATEGORIE - Final abgesagt
  });
  const [leadsLoading, setLeadsLoading] = useState(false);

  const navigate = useNavigate();

  // Rolleninformationen
  const isAdmin = user?.isAdmin || false;
  const isTeamlead = user?.isTeamlead || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;
  const isFachberater = user?.isFachberater || false;
  const isAgenturLeader = user?.isAgenturLeader || false;

  const loadLeadCounts = async () => {
    if (!user?.organization_id || (!isCallcenterLeader && !isCallcenterMitarbeiter)) {
      return;
    }

    setLeadsLoading(true);
    
    try {
      const response = await getOrganizationLeads(user.id, user.organization_id);
      
      if (response.success) {
        const allLeads = response.leads || [];

        const counts = {
          all: allLeads.filter(lead =>
            lead.status_pv !== 'Test Lead' && lead.status_wp !== 'Test Lead'
          ).length,

          // Offene Leads: Mit Opt-In UND noch nicht angerufen
          open: (() => {
            const filtered = allLeads.filter(lead => 
              (lead.signature || lead.opt_in) && // Hat Opt-In
              (!lead.has_calls || lead.call_count === 0) && // Noch nicht angerufen
              lead.status_pv !== 'Test Lead' && // Kein PV Test Lead
              lead.status_wp !== 'Test Lead' && // Kein WP Test Lead
              lead.customer_status !== 'Termin Storniert' // Kein stornierter Termin
            );
            console.log('Offene Leads:', filtered);
            return filtered.length;
          })(),

          // Ohne Opt-In: Leads ohne Opt-In die noch nicht angerufen wurden
          ohneOptIn: allLeads.filter(lead => 
            (!lead.signature || lead.signature === '') && // Keine Signature
            (!lead.opt_in || lead.opt_in === 0) && // Kein Opt-In
            lead.status_pv !== 'Test Lead' && // Kein PV Test Lead
            lead.status_wp !== 'Test Lead' && // Kein WP Test Lead
            lead.name !== 'BLOCKIERT'
          ).length,

          // On Hold: erstmal leer (für zukünftige Features)
          onHold: allLeads.filter(lead => 
            lead.customer_status === 'On Hold' &&
            lead.status_pv !== 'Test Lead' && // Kein PV Test Lead
            lead.status_wp !== 'Test Lead' // Kein WP Test Lead
          ).length,

          // Termin Abgesagt: Leads mit abgesagten Terminen
          terminAbgesagt: allLeads.filter(lead => 
            lead.customer_status === 'Termin Storniert' &&
            lead.status_pv !== 'Test Lead' && // Kein PV Test Lead
            lead.status_wp !== 'Test Lead' // Kein WP Test Lead
          ).length,

          // Final Abgesagt: Leads mit Final abgesagt Status
          finalAbgesagt: allLeads.filter(lead => 
            lead.customer_status === 'Final abgesagt' &&
            lead.status_pv !== 'Test Lead' && // Kein PV Test Lead
            lead.status_wp !== 'Test Lead' // Kein WP Test Lead
          ).length,

          // NE Kunden: Kunden mit "Nicht erreicht" Anruf-Status
          // NE Kunden: Kunden mit "Nicht erreicht" Anruf-Status
          neKunden: (() => {
            const filtered = allLeads.filter(lead => {
              if (!Array.isArray(lead.calls)) return false;
              
              const count = lead.calls.filter(call => call.status === 'Nicht erreicht').length;
              
              // Hartes Limit: 15 oder mehr = raus
              if (count >= 15) return false;
              
              return (lead.last_call_status === 'Nicht erreicht' || lead.last_call_status === 'Abgelehnt') &&
                lead.customer_status !== 'On Hold' &&
                lead.customer_status !== 'Final abgesagt' &&
                lead.customer_status !== 'Final nicht erreicht' &&
                lead.status_pv !== 'Test Lead' &&
                lead.status_wp !== 'Test Lead';
            });
            
            console.log('NE Kunden (Anzahl):', filtered.length);
            console.log('NE Kunden (Details):', filtered.map(lead => ({
              id: lead.id,
              name: lead.name || `${lead.vorname} ${lead.nachname}`,
              last_call_status: lead.last_call_status,
              nicht_erreicht_count: lead.calls.filter(call => call.status === 'Nicht erreicht').length,
              customer_status: lead.customer_status,
              status_pv: lead.status_pv,
              status_wp: lead.status_wp
            })));
            
            return filtered.length;
          })(),

          // Später anrufen: Kunden mit "Später anrufen" oder "Rückruf erwünscht" Status
          spaeterAnrufen: allLeads.filter(lead => 
            (lead.last_call_status === 'Rückruf erwünscht' ||
             lead.last_call_status === 'Später anrufen') &&
            lead.customer_status !== 'On Hold' &&
            lead.customer_status !== 'Final abgesagt' &&
            lead.customer_status !== 'Final nicht erreicht' &&
            lead.status_pv !== 'Test Lead' &&
            lead.status_wp !== 'Test Lead'
        ).length,

          // Test Leads: Alle Leads mit Test Lead Status
          testLeads: allLeads.filter(lead => 
            lead.status_pv === 'Test Lead' || lead.status_wp === 'Test Lead'
          ).length,

          // Final nicht erreicht: Leads mit mehr als 14x "Nicht erreicht" UND letzter Status ist auch "Nicht erreicht"
          finalNichtErreicht: allLeads.filter(lead => {
            let count = 0;
            if (Array.isArray(lead.calls)) {
              count = lead.calls.filter(call => call.status === 'Nicht erreicht').length;
            } else {
              count = lead.nicht_erreicht_count || (lead.call_status_counts && lead.call_status_counts['Nicht erreicht']) || 0;
            }
            
            // Nur zählen wenn mehr als 14 "Nicht erreicht" Anrufe UND der letzte Status auch "Nicht erreicht" ist
            return count > 14 && 
                   (lead.last_call_status === 'Nicht erreicht' || lead.last_call_status === 'Abgelehnt') &&
                   lead.status_pv !== 'Test Lead' && 
                   lead.status_wp !== 'Test Lead';
          }).length
        };
        
        setLeadCounts(counts);

        // Debug: Zeige alle Leads, die mehr als 14 'Nicht erreicht'-Anrufe haben
        const leadsMitZuVielenNichtErreicht = allLeads.filter(lead => {
          if (!Array.isArray(lead.calls)) return false;
          const count = lead.calls.filter(call => call.status === 'Nicht erreicht').length;
          return count >= 14;
        });
        if (leadsMitZuVielenNichtErreicht.length > 0) {
          console.log('Leads mit >= 14 Nicht erreicht:', leadsMitZuVielenNichtErreicht);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Lead-Zähler:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  useEffect(() => {
    // Nur für Callcenter-Rollen Lead-Zähler laden
    if (!isFachberater) {
      loadLeadCounts();
    }
  }, [user?.id, user?.organization_id, isCallcenterLeader, isCallcenterMitarbeiter, isFachberater]);

  // Tab-Titel basierend auf Organisation aktualisieren
  useEffect(() => {
    if (user?.organization_id) {
      updateTabTitle(user.organization_id);
    }
  }, [user?.organization_id]);

  const openSettingsPanel = () => {
    setSettingsPanelOpen(true);
  };

  const closeSettingsPanel = () => {
    setSettingsPanelOpen(false);
  };

  // Handler für verschiedene Aktionen
  const handleViewOrganizationLeads = (orgId) => {
    if (typeof onViewOrganizationLeads === 'function') {
      onViewOrganizationLeads(orgId);
      closeSettingsPanel();
    } else {
      console.error("onViewOrganizationLeads function not available");
      alert("Funktion nicht verfügbar");
    }
  };

  // Handler für Callcenter Dashboard
  const handleCallcenterDashboard = () => {
    onSectionChange('callcenter-dashboard');
  };

  // NEUE FILTER-LOGIK (nur für Callcenter-Rollen)
  const handleCustomerFilter = (filter) => {
    switch (filter) {
      case 'all':
        onSectionChange('leads');
        break;
      case 'open':
        onSectionChange('leads', { filter: 'open' });
        break;
      case 'ohne-opt-in': // NEUER FILTER - Ohne Opt-In
        onSectionChange('leads', { filter: 'ohne-opt-in' });
        break;
      case 'on-hold': // Neue Kategorie
        onSectionChange('leads', { filter: 'on-hold' });
        break;
      case 'termin-abgesagt': // Neue Kategorie
        onSectionChange('leads', { filter: 'termin-abgesagt' });
        break;
      case 'ne-kunden': // Neue Kategorie - Nicht erreichte Kunden
        onSectionChange('leads', { filter: 'ne-kunden' });
        break;
      case 'spaeter-anrufen': // Neue Kategorie - Später anrufen
        onSectionChange('leads', { filter: 'spaeter-anrufen' });
        break;
      case 'final-nicht-erreicht': // Neue Kategorie - Final nicht erreicht
        onSectionChange('leads', { filter: 'final-nicht-erreicht' });
        break;
      case 'final-abgesagt': // Neue Kategorie - Final abgesagt
        onSectionChange('leads', { filter: 'final-abgesagt' });
        break;
      default:
        onSectionChange('leads');
    }
  };

  const handleTeamDashboard = () => {
    onSectionChange('team-dashboard');
  };

  // Props für Navigation Components
  const navigationProps = {
    activeSection,
    onSectionChange,
    onLogout,
    user,
    isAdmin,
    isTeamlead,
    isOrgLeader,
    isCallcenterLeader,
    isCallcenterMitarbeiter,
    isFachberater, // Neue Rolle hinzugefügt
    isAgenturLeader, // Ergänzt für Agenturleiter
    leadCounts,
    leadsLoading,
    openSettingsPanel,
    handleCustomerFilter,
    handleTeamDashboard,
    handleViewOrganizationLeads,
    onOpenOrganizationManagement,
    onOpenMembersManagement,
    onViewTeamLeads,
    navigate,
    // Erweiterte Device Info
    deviceInfo,
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    orientation,
    handleCallcenterDashboard
  };

  // Dynamische Navbar Styles basierend auf Gerät
  const getNavbarStyles = () => {
    const baseStyles = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    };

    // Gerätespezifische Styles
    if (isMobile) {
      return {
        ...baseStyles,
        padding: '10px 12px',
        flexWrap: orientation === 'portrait' ? 'wrap' : 'nowrap'
      };
    } else if (isTablet) {
      return {
        ...baseStyles,
        padding: '12px 16px'
      };
    } else {
      return {
        ...baseStyles,
        padding: '15px 20px'
      };
    }
  };

  // Entscheide welche Navigation zu verwenden
  const shouldUseMobileNav = isMobile || (isTablet && orientation === 'portrait');

  return (
    <>
      <div className="dashboard-navbar" style={getNavbarStyles()}>
        {shouldUseMobileNav ? (
          <MobileNavigation {...navigationProps} />
        ) : (
          <DesktopNavigation {...navigationProps} />
        )}
      </div>

      {/* Settings Panel */}
      {settingsPanelOpen && (
        <SettingsPanel 
          isOpen={settingsPanelOpen} 
          onClose={closeSettingsPanel} 
          isAdmin={isAdmin} 
          isTeamlead={isTeamlead}
          isOrgLeader={isOrgLeader}
          isCallcenterLeiter={isCallcenterLeader}
          isCallcenterMitarbeiter={isCallcenterMitarbeiter}
          isFachberater={isFachberater}
          isAgenturLeader={isAgenturLeader}
          user={user}
          onOpenOrganizationManagement={onOpenOrganizationManagement || (() => {})}
          onOpenMembersManagement={onOpenMembersManagement || (() => {})}
          onViewOrganizationLeads={handleViewOrganizationLeads}
        />
      )}
      
      {/* Overlay */}
      <div 
        className={`settings-overlay ${settingsPanelOpen ? 'open' : ''}`} 
        onClick={closeSettingsPanel}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999,
          opacity: settingsPanelOpen ? 1 : 0,
          visibility: settingsPanelOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
        }}
      />
    </>
  );
}

export default DashboardNav;